import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import CrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import Input from '../../components/Input';
import DatePicker from '../../components/DatePicker';
import moment from 'moment';
import ArrowRightIcon from '../../assets/icons/arrow-right.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import Select from '../../components/Select';
import Pagination from '../../components/CustomPagination';
import { Link } from 'react-router';
import { useLocalStorage } from '@uidotdev/usehooks';
import { LoginResponseData } from '@EHSNavigator/backend';

interface BulkUserCreationStepTwoProps {
  otherClasses?: string;
  formHandler: any;
  handleSteps: React.Dispatch<number>;
  departments: {
    label: string;
    value: string;
  }[];
  roles: {
    label: string;
    value: string;
  }[];
  currentPage: number;
  setCurrentPage: React.Dispatch<number>;
  loading?: boolean;
  notExistDepartment: string[];
}

const BulkUserCreationStepTwo: React.FC<BulkUserCreationStepTwoProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  departments,
  roles,
  currentPage,
  setCurrentPage,
  notExistDepartment,
  loading,
}) => {
  const bulkUserCreationStepTwoClasses = clsx(
    otherClasses,
    'h-full flex flex-col ',
  );
  const dispatch = useDispatch();
  const lg = useButtonSizeLarge();
  const [userInfo] = useLocalStorage<LoginResponseData | null>('userInfo');

  const handleRowDelete = (index: number) => {
    const userRecords = formHandler.values.userRecords || [];
    const newUserRecords = userRecords.filter(
      (_: any, i: number) => i !== index,
    );
    formHandler.setFieldValue('userRecords', newUserRecords, true);
  };

  const uniqueArr = notExistDepartment.filter(
    (item, index) => notExistDepartment.indexOf(item) === index,
  );

  const notExistDepartmentNames = uniqueArr
    ?.filter(
      (department) =>
        department.trim() !== '' &&
        !departments.some(
          (d) =>
            d.label?.toLowerCase().trim() === department.toLowerCase().trim(),
        ),
    )
    .join(', ');

  return (
    <div
      className={bulkUserCreationStepTwoClasses}
      data-testid="bulk-user-creation-step-two"
    >
      <div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <h1 className="h3">Previewing User Details</h1>
        </div>
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 text-lg leading-5 font-medium lg:block lg:text-xl lg:leading-6">
          Verify the uploaded user details and assign appropriate departments,
          job titles, and roles before proceeding.
        </p>
      </div>
      <div className="rounded-10 bg-card-backgrounds/5 border-neutral-tones-text/10 mt-12 flex w-full flex-1 flex-col items-start border px-15">
        <div className="global-scrollbar-style mb-2 flex w-full flex-1 flex-col items-start overflow-auto px-1 pt-15 pb-14">
          <h5 className="text-brand-byzantine font-inter-variable text-xl font-bold">
            Employees Details
          </h5>
          {notExistDepartmentNames && (
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 min-w-7xl text-lg leading-5 font-medium lg:block lg:text-xl lg:leading-6">
              <WarningIcon className="inline-block -translate-y-0.5" />{' '}
              <strong className="text-brand-posh-pink font-bold">Note: </strong>{' '}
              Department(s){' '}
              <strong className="text-brand-posh-pink font-bold">
                {`(${notExistDepartmentNames})`}
              </strong>{' '}
              does not exist in your organization{' '}
              <strong className="text-brand-byzantine font-bold">
                {`(${userInfo?.user?.org?.name})`}
              </strong>{' '}
              click{' '}
              <Link
                to="/departments"
                className="text-brand-byzantine underline"
              >
                here
              </Link>{' '}
              to add new department, or select from the dropdown.
            </p>
          )}
          <div className="w-full min-w-7xl pt-9">
            <div className="border-neutral-tones-text/10 grid grid-cols-9 gap-3 border-b pb-5">
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Employee ID
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Full Name
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Email
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Number
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Date of Birth
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Date of Hire
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Department
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Job Title
              </h6>
              <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-bold">
                Role
              </h6>
            </div>
            <div className="bulk-user-table">
              {formHandler?.values?.userRecords
                ?.slice((currentPage - 1) * 10, currentPage * 10)
                .map((node: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className="border-neutral-tones-text/10 mt-5 grid grid-cols-9 gap-3 border-b pb-5"
                    >
                      <Input
                        onBlur={formHandler.handleBlur}
                        errorMessage={
                          formHandler?.touched.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.employeeId &&
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.employeeId
                            ? formHandler.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].employeeId
                            : ''
                        }
                        disabled={formHandler.isSubmitting}
                        value={node?.employeeId}
                        type="text"
                        placeholder="Employee ID"
                        onChange={(e) => {
                          formHandler.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.employeeId`,
                            e.target.value,
                          );
                        }}
                        name={`userRecords.${(currentPage - 1) * 10 + index}.employeeId`}
                        otherClasses={{
                          container: 'w-full',
                          input:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md',
                        }}
                      />
                      <Input
                        onBlur={formHandler.handleBlur}
                        errorMessage={
                          formHandler?.touched.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.fullName &&
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.fullName
                            ? formHandler.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].fullName
                            : ''
                        }
                        disabled={formHandler.isSubmitting}
                        value={node?.fullName}
                        type="text"
                        placeholder="Full Name"
                        onChange={(e) => {
                          formHandler.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.fullName`,
                            e.target.value,
                          );
                        }}
                        name={`userRecords.${(currentPage - 1) * 10 + index}.fullName`}
                        otherClasses={{
                          container: 'w-full',
                          input:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md',
                        }}
                      />
                      <Input
                        onBlur={formHandler.handleBlur}
                        errorMessage={
                          formHandler?.touched.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.email &&
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.email
                            ? formHandler.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].email
                            : ''
                        }
                        disabled={formHandler.isSubmitting}
                        value={node?.email}
                        type="text"
                        placeholder="Email"
                        onChange={(e) => {
                          formHandler.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.email`,
                            e.target.value,
                          );
                        }}
                        name={`userRecords.${(currentPage - 1) * 10 + index}.email`}
                        otherClasses={{
                          container: 'w-full',
                          input:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md truncate',
                        }}
                      />
                      <Input
                        onBlur={formHandler.handleBlur}
                        errorMessage={
                          formHandler?.touched.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.contactNumber &&
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.contactNumber
                            ? formHandler.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].contactNumber
                            : ''
                        }
                        disabled={formHandler.isSubmitting}
                        value={node?.contactNumber}
                        type="text"
                        placeholder="Contact Number"
                        onChange={(e) => {
                          formHandler.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.contactNumber`,
                            e.target.value,
                          );
                        }}
                        name={`userRecords.${(currentPage - 1) * 10 + index}.contactNumber`}
                        otherClasses={{
                          container: 'w-full',
                          input:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md',
                        }}
                      />
                      <DatePicker
                        otherClasses={{
                          container:
                            'w-full [&>button>svg]:w-4 [&>button>svg]:h-4 [&>button]:top-2/4 [&>button]:right-2',
                          input:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md truncate',
                          dropdownMainContainer:
                            ' !pt-1.5 !px-3 !pb-1.5 date-picker-dropdown',
                        }}
                        customDate
                        name={`userRecords.${(currentPage - 1) * 10 + index}.dateOfBirth`}
                        value={
                          node?.dateOfBirth
                            ? moment(node.dateOfBirth).format('MMM DD, YYYY')
                            : ''
                        }
                        startMonth={moment([1950, 0]).toDate()}
                        // endMonth={moment().subtract(18, 'years').toDate()}
                        endMonth={moment().toDate()}
                        placeholder="Select Date"
                        onBlur={formHandler.handleBlur}
                        disabled={formHandler.isSubmitting}
                        onChange={(e: any) => {
                          formHandler?.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.dateOfHire`,
                            '',
                            true,
                          );
                          formHandler?.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.dateOfBirth`,
                            moment(new Date(e)).isValid()
                              ? moment(new Date(e)).format('YYYY-MM-DD')
                              : '',
                            true,
                          );
                        }}
                        errorMessage={
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.dateOfBirth
                            ? formHandler?.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].dateOfBirth
                            : ''
                        }
                      />
                      <DatePicker
                        otherClasses={{
                          container:
                            'w-full [&>button>svg]:w-4 [&>button>svg]:h-4 [&>button]:top-2/4 [&>button]:right-2',
                          input:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md truncate',
                          dropdownMainContainer:
                            ' !pt-1.5 !px-3 !pb-1.5 date-picker-dropdown',
                        }}
                        customDate
                        name={`userRecords.${(currentPage - 1) * 10 + index}.dateOfHire`}
                        value={
                          node?.dateOfHire
                            ? moment(node.dateOfHire).format('MMM DD, YYYY')
                            : ''
                        }
                        // startMonth={
                        //   node?.dateOfBirth
                        //     ? moment(node.dateOfBirth).add(18, 'years').toDate()
                        //     : new Date(1950, 0)
                        // }
                        startMonth={new Date(1950, 0)}
                        endMonth={new Date()}
                        placeholder="Select Date"
                        onBlur={formHandler.handleBlur}
                        disabled={formHandler.isSubmitting}
                        onChange={(e: any) => {
                          formHandler?.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.dateOfHire`,
                            moment(new Date(e)).isValid()
                              ? moment(new Date(e)).format('YYYY-MM-DD')
                              : '',
                            true,
                          );
                        }}
                        errorMessage={
                          formHandler?.touched.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.dateOfHire &&
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.dateOfHire
                            ? formHandler?.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].dateOfHire
                            : ''
                        }
                      />
                      <Select
                        list={departments}
                        value={node?.department}
                        disabled={formHandler.isSubmitting}
                        placeholder="Department"
                        otherClasses={{
                          container: 'w-full',
                          listContainer: ' !px-2 !pt-2 !pb-2 !max-h-40 !top-10',
                          list: '[&>button]:text-xs [&>button]:leading-3 !py-1',
                          button:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md [&>svg]:w-4 [&>svg]:h-4',
                        }}
                        onSelect={(e) =>
                          formHandler?.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.department`,
                            e,
                            true,
                          )
                        }
                        errorMessage={
                          formHandler?.touched.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.department &&
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.department
                            ? formHandler?.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].department
                            : ''
                        }
                      />
                      <Input
                        onBlur={formHandler.handleBlur}
                        errorMessage={
                          formHandler?.touched.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.jobTitle &&
                          formHandler?.errors.userRecords?.[
                            (currentPage - 1) * 10 + index
                          ]?.jobTitle
                            ? formHandler.errors.userRecords[
                                (currentPage - 1) * 10 + index
                              ].jobTitle
                            : ''
                        }
                        disabled={formHandler.isSubmitting}
                        value={node?.jobTitle}
                        type="text"
                        placeholder="Job Title"
                        onChange={(e) => {
                          formHandler.setFieldValue(
                            `userRecords.${(currentPage - 1) * 10 + index}.jobTitle`,
                            e.target.value,
                          );
                        }}
                        name={`userRecords.${(currentPage - 1) * 10 + index}.jobTitle`}
                        otherClasses={{
                          container: 'w-full',
                          input:
                            '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md',
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <Select
                          list={roles}
                          value={node?.role}
                          disabled={formHandler.isSubmitting}
                          placeholder="Role"
                          otherClasses={{
                            container: 'w-full',
                            listContainer: '!top-10 !px-2 !pt-2 !pb-2',
                            list: '[&>button]:text-xs [&>button]:leading-3 !py-1',
                            button:
                              '!p-2 !h-fit text-sm font-normal leading-4 bg-white dark:bg-transparent rounded-md [&>svg]:w-4 [&>svg]:h-4',
                          }}
                          onSelect={(e) =>
                            formHandler?.setFieldValue(
                              `userRecords.${(currentPage - 1) * 10 + index}.role`,
                              e,
                              true,
                            )
                          }
                          errorMessage={
                            formHandler?.touched.userRecords?.[
                              (currentPage - 1) * 10 + index
                            ]?.role &&
                            formHandler?.errors.userRecords?.[
                              (currentPage - 1) * 10 + index
                            ]?.role
                              ? formHandler?.errors.userRecords[
                                  (currentPage - 1) * 10 + index
                                ].role
                              : ''
                          }
                        />
                        {formHandler?.values?.userRecords?.length !== 1 && (
                          <Button
                            type="button"
                            onClick={() => {
                              handleRowDelete((currentPage - 1) * 10 + index);
                              const totalRecords =
                                formHandler?.values?.userRecords?.length ?? 0;
                              const recordsOnCurrentPage =
                                totalRecords - (currentPage - 1) * 10;
                              if (
                                recordsOnCurrentPage <= 1 && // because one will be deleted
                                currentPage > 1
                              ) {
                                setCurrentPage(currentPage - 1);
                              }
                            }}
                            otherClasses="!w-6 !h-6 min-w-6 min-h-6 !p-0 !rounded-full"
                          >
                            <CrossIcon className="h-3 w-3 rotate-180 [&>g>path]:fill-current" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          <div className="border-neutral-tones-text/10 mt-auto w-full min-w-7xl border-b py-6">
            <Button
              type="button"
              otherClasses=""
              size="small"
              onClick={() => {
                const prev = formHandler.values.userRecords ?? [];
                const newUserRecords = [
                  ...prev,
                  {
                    employeeId: '',
                    fullName: '',
                    email: '',
                    contactNumber: '',
                    dateOfBirth: '',
                    department: '',
                    jobTitle: '',
                    role: '',
                  },
                ];
                formHandler.setFieldValue('userRecords', newUserRecords);
                const newTotalPages = Math.ceil(newUserRecords.length / 10);
                if (currentPage !== newTotalPages) {
                  setCurrentPage(newTotalPages);
                }
              }}
            >
              Add Row
              <AddIcon className="h-6 w-6 [&>path]:fill-current" />
            </Button>
          </div>
        </div>
        {formHandler?.values?.userRecords?.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(
              formHandler?.values?.userRecords?.length / 10,
            )}
            totalItems={formHandler?.values?.userRecords?.length}
            limit={10}
            showingItems={
              formHandler?.values?.userRecords?.slice(
                (currentPage - 1) * 10,
                currentPage * 10,
              )?.length
            }
            onPageChange={(page) => setCurrentPage(page)}
            hiddenCombobox={true}
            otherClasses="!pb-15 mt-auto !pt-0 "
          />
        )}
      </div>
      <div className="mt-10 flex items-center justify-center gap-6 lg:justify-between">
        <Button
          size={lg}
          type="button"
          variant="secondary"
          onClick={() => handleSteps(1)}
          disable={formHandler?.values?.file?.length === 0 || loading}
          otherClasses="w-full sm:w-150"
        >
          <ArrowRightIcon className="h-6 w-6 rotate-180 [&>g>path]:fill-current" />
          Back
        </Button>
        <Button
          size={lg}
          type="button"
          onClick={() => handleSteps(3)}
          disable={formHandler?.values?.file?.length === 0 || loading}
          loading={loading}
          otherClasses="w-full sm:w-150"
        >
          Next <ArrowRightIcon className="h-6 w-6 [&>g>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default BulkUserCreationStepTwo;
